import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthStore } from "@/stores/auth/auth-store";
import {
  useOrganizationStore,
  type OwnerContext,
} from "@/stores/organization/organization-store";
import { isRecord } from "@/types/componentDataBinding";
import type {
  CatalogSourceReconnectReason,
  CatalogSourceResolveOperation,
  CatalogSourceResolveResponse,
  DataSourceRef,
} from "@/types/componentDataBinding";
import {
  queryComponentSchemaBinding,
  resolveComponentSchemaSource,
} from "@/services/api/componentSchemaBindingApi";

interface UseSchemaBindingDataOptions {
  source: DataSourceRef | null;
  enabled?: boolean;
  limit?: number;
}

export interface UseSchemaBindingDataResult {
  data: Record<string, unknown>[] | null;
  isLoading: boolean;
  error: string | null;
  sourceErrorCode?: CatalogSourceReconnectReason;
  refetch: () => Promise<void>;
  lastFetched: Date | null;
}

interface UseSchemaBindingSourceResolutionOptions {
  source: DataSourceRef | null;
  operation: CatalogSourceResolveOperation;
  enabled?: boolean;
}

export interface UseSchemaBindingSourceResolutionResult {
  sourceResolution: CatalogSourceResolveResponse | null;
  writable: boolean;
  isLoading: boolean;
  error: string | null;
  sourceErrorCode?: CatalogSourceReconnectReason;
  refetch: () => Promise<void>;
  lastFetched: Date | null;
}

const DEFAULT_LIMIT = 100;

function getTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getRequestingOrganizationId(
  currentContext: OwnerContext | null,
): string | null {
  if (currentContext?.type === "organization") {
    const contextId = getTrimmedString(currentContext.id);
    if (contextId !== null) {
      return contextId;
    }
  }

  return null;
}

function getRecordArray(value: unknown): Record<string, unknown>[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.every(isRecord) ? value : null;
}

function getReconnectReason(value: unknown): CatalogSourceReconnectReason | null {
  return value === "missing-source-scope" ||
    value === "source-inaccessible" ||
    value === "source-owner-scope-mismatch" ||
    value === "source-permission-denied" ||
    value === "source-write-authority-required" ||
    value === "source-unavailable"
    ? value
    : null;
}

function getErrorDetails(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error)) {
    return null;
  }

  if (isRecord(error.details)) {
    return error.details;
  }

  if (!isRecord(error.response)) {
    return null;
  }

  const data = error.response.data;
  if (!isRecord(data)) {
    return null;
  }

  return isRecord(data.details) ? data.details : null;
}

function getSourceErrorCode(error: unknown): CatalogSourceReconnectReason {
  const details = getErrorDetails(error);
  const directReason = getReconnectReason(details?.reconnectReason);
  if (directReason !== null) {
    return directReason;
  }

  if (isRecord(details?.diagnostics)) {
    const diagnosticReason = getReconnectReason(
      details.diagnostics.reconnectReason,
    );
    if (diagnosticReason !== null) {
      return diagnosticReason;
    }
  }

  if (isRecord(error) && error.status === 403) {
    return "source-permission-denied";
  }

  if (isRecord(error) && error.status === 404) {
    return "source-unavailable";
  }

  return "source-unavailable";
}

function getErrorMessage(error: unknown): string {
  if (isRecord(error) && typeof error.message === "string") {
    return error.message;
  }

  return "Schema binding query failed.";
}

function getFetchKey(
  source: DataSourceRef | null,
  requestingOrganizationId: string | null,
  limit: number,
): string {
  if (source === null) {
    return "schema:none";
  }

  return [
    "schema",
    source.dataOwnerOrganizationId,
    requestingOrganizationId ?? "requester:none",
    source.groupId,
    source.schemaId,
    String(limit),
  ].join(":");
}

function getResolutionFetchKey(
  source: DataSourceRef | null,
  requestingOrganizationId: string | null,
  operation: CatalogSourceResolveOperation,
): string {
  if (source === null) {
    return "schema-resolution:none";
  }

  return [
    "schema-resolution",
    operation,
    source.dataOwnerOrganizationId,
    requestingOrganizationId ?? "requester:none",
    source.groupId,
    source.schemaId,
  ].join(":");
}

function hasRequiredSourceScope(source: DataSourceRef): boolean {
  return (
    getTrimmedString(source.dataOwnerOrganizationId) !== null &&
    getTrimmedString(source.groupId) !== null &&
    getTrimmedString(source.schemaId) !== null
  );
}

export function useSchemaBindingData({
  source,
  enabled = true,
  limit = DEFAULT_LIMIT,
}: UseSchemaBindingDataOptions): UseSchemaBindingDataResult {
  const [data, setData] = useState<Record<string, unknown>[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceErrorCode, setSourceErrorCode] = useState<
    CatalogSourceReconnectReason | undefined
  >();
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const isMountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const lastFetchedKeyRef = useRef<string | null>(null);
  const authInitialized = useAuthStore((state) => state.initialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const currentContext = useOrganizationStore((state) => state.currentContext);
  const authReady =
    authInitialized &&
    isAuthenticated &&
    typeof accessToken === "string" &&
    accessToken.length > 0;

  const requestingOrganizationId = getRequestingOrganizationId(currentContext);
  const fetchKey = useMemo(
    () => getFetchKey(source, requestingOrganizationId, limit),
    [limit, requestingOrganizationId, source],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const executeQuery = useCallback(
    async (force = false) => {
      if (!enabled || source === null) {
        setData(null);
        setIsLoading(false);
        setError(null);
        setSourceErrorCode(undefined);
        return;
      }

      if (!hasRequiredSourceScope(source)) {
        setData(null);
        setIsLoading(false);
        setError("Schema binding is missing source scope and requires reconnection.");
        setSourceErrorCode("missing-source-scope");
        return;
      }

      if (requestingOrganizationId === null) {
        setData(null);
        setIsLoading(false);
        setError("Schema binding requires an authenticated organization context.");
        setSourceErrorCode("missing-source-scope");
        return;
      }

      if (!authReady) {
        return;
      }

      if (isFetchingRef.current) {
        return;
      }

      if (!force && lastFetchedKeyRef.current === fetchKey) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      setSourceErrorCode(undefined);

      try {
        const response = await queryComponentSchemaBinding({
          dataOwnerOrganizationId: source.dataOwnerOrganizationId,
          requestingOrganizationId,
          groupId: source.groupId,
          schemaId: source.schemaId,
          limit,
        });
        if (!isMountedRef.current) {
          return;
        }

        const records = getRecordArray(response.data);
        if (records === null) {
          setData(null);
          setError("Schema binding query returned malformed rows.");
          setSourceErrorCode("source-unavailable");
          return;
        }

        lastFetchedKeyRef.current = fetchKey;
        setData(records);
        setLastFetched(new Date());
      } catch (queryError: unknown) {
        if (!isMountedRef.current) {
          return;
        }

        setData(null);
        setError(getErrorMessage(queryError));
        setSourceErrorCode(getSourceErrorCode(queryError));
      } finally {
        isFetchingRef.current = false;
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      authReady,
      enabled,
      fetchKey,
      limit,
      requestingOrganizationId,
      source,
    ],
  );

  useEffect(() => {
    void executeQuery(false);
  }, [executeQuery]);

  const refetch = useCallback(async () => {
    await executeQuery(true);
  }, [executeQuery]);

  return {
    data,
    isLoading,
    error,
    sourceErrorCode,
    refetch,
    lastFetched,
  };
}

export function useSchemaBindingSourceResolution({
  source,
  operation,
  enabled = true,
}: UseSchemaBindingSourceResolutionOptions): UseSchemaBindingSourceResolutionResult {
  const [sourceResolution, setSourceResolution] =
    useState<CatalogSourceResolveResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceErrorCode, setSourceErrorCode] = useState<
    CatalogSourceReconnectReason | undefined
  >();
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const isMountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const lastFetchedKeyRef = useRef<string | null>(null);
  const authInitialized = useAuthStore((state) => state.initialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const currentContext = useOrganizationStore((state) => state.currentContext);
  const authReady =
    authInitialized &&
    isAuthenticated &&
    typeof accessToken === "string" &&
    accessToken.length > 0;

  const requestingOrganizationId = getRequestingOrganizationId(currentContext);
  const fetchKey = useMemo(
    () => getResolutionFetchKey(source, requestingOrganizationId, operation),
    [operation, requestingOrganizationId, source],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const executeResolution = useCallback(
    async (force = false) => {
      if (!enabled || source === null) {
        setSourceResolution(null);
        setIsLoading(false);
        setError(null);
        setSourceErrorCode(undefined);
        return;
      }

      if (!hasRequiredSourceScope(source)) {
        setSourceResolution(null);
        setIsLoading(false);
        setError("Write target is missing source scope and requires reconnection.");
        setSourceErrorCode("missing-source-scope");
        return;
      }

      if (requestingOrganizationId === null) {
        setSourceResolution(null);
        setIsLoading(false);
        setError("Write target requires an authenticated organization context.");
        setSourceErrorCode("missing-source-scope");
        return;
      }

      if (!authReady) {
        return;
      }

      if (isFetchingRef.current) {
        return;
      }

      if (!force && lastFetchedKeyRef.current === fetchKey) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      setSourceErrorCode(undefined);

      try {
        const resolved = await resolveComponentSchemaSource({
          source,
          requestingOrganizationId,
          operation,
        });
        if (!isMountedRef.current) {
          return;
        }

        lastFetchedKeyRef.current = fetchKey;
        setSourceResolution(resolved);
        setLastFetched(new Date());
        setError(resolved.error?.message ?? null);
        setSourceErrorCode(resolved.error?.code ?? undefined);
      } catch (resolutionError: unknown) {
        if (!isMountedRef.current) {
          return;
        }

        setSourceResolution(null);
        setError(getErrorMessage(resolutionError));
        setSourceErrorCode(getSourceErrorCode(resolutionError));
      } finally {
        isFetchingRef.current = false;
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      authReady,
      enabled,
      fetchKey,
      operation,
      requestingOrganizationId,
      source,
    ],
  );

  useEffect(() => {
    void executeResolution(false);
  }, [executeResolution]);

  const refetch = useCallback(async () => {
    await executeResolution(true);
  }, [executeResolution]);

  return {
    sourceResolution,
    writable:
      sourceResolution?.capabilities.writable === true &&
      sourceResolution.error === null,
    isLoading,
    error,
    sourceErrorCode,
    refetch,
    lastFetched,
  };
}
